import { Modal } from '@/components/ui/modal';
import { Text } from '@/components/ui/text';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useAppTheme } from '@/providers/theme-provider';
import type { LikerUser } from '@/types/feed';
import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import {
    ActivityIndicator,
    FlatList,
    Pressable,
    View,
} from 'react-native';
import { useCheckInLikers } from '../hooks/use-check-in-likers';

interface LikesModalProps {
  visible: boolean;
  onClose: () => void;
  checkInId: string;
}

export function LikesModal({ visible, onClose, checkInId }: LikesModalProps) {
  const theme = useAppTheme();
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useCheckInLikers(checkInId, visible);

  const allLikers = data?.pages.flatMap((page) => page.items) ?? [];

  const renderItem = ({ item }: { item: LikerUser }) => {
    return (
      <View className="flex-row items-center py-3 px-4">
        <UserAvatar
          uri={item.avatarUrl}
          name={item.displayName || item.username}
          size={40}
          style={{ marginRight: 12 }}
        />
        <View className="flex-1">
          <Text className="font-medium text-foreground">
            {item.displayName || item.username || 'User'}
          </Text>
          {item.username && (
            <Text className="text-sm text-muted-foreground">
              @{item.username}
            </Text>
          )}
        </View>
      </View>
    );
  };

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      animationType="slide"
    >
      <View className="bg-surface" style={{ borderRadius: 12, maxHeight: 400 }}>
        {/* Header */}
        <View className="flex-row items-center justify-between px-4 py-3 border-b border-border">
          <Text className="text-lg font-semibold text-foreground">Likes</Text>
          <Pressable
            onPress={onClose}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            accessibilityRole="button"
            accessibilityLabel="Close"
          >
            <Ionicons name="close" size={24} className="text-muted-foreground" />
          </Pressable>
        </View>

        {/* Content */}
        {isLoading ? (
          <View className="py-8 items-center">
            <ActivityIndicator color={theme.colors.primary} />
          </View>
        ) : allLikers.length === 0 ? (
          <View className="py-8 items-center">
            <Text className="text-sm text-muted-foreground">No likes yet</Text>
          </View>
        ) : (
          <FlatList
            data={allLikers}
            renderItem={renderItem}
            keyExtractor={(item) => item.id}
            onEndReached={() => {
              if (hasNextPage && !isFetchingNextPage) {
                fetchNextPage();
              }
            }}
            onEndReachedThreshold={0.5}
            ListFooterComponent={
              isFetchingNextPage ? (
                <View className="py-3 items-center">
                  <ActivityIndicator size="small" color={theme.colors.primary} />
                </View>
              ) : null
            }
            style={{ maxHeight: 340 }}
          />
        )}
      </View>
    </Modal>
  );
}
